import React, { useEffect, useState } from "react";
import "../../styles/app-notification.css";
import { Tabs, Tab, CircularProgress, Box, Menu, MenuItem } from "@mui/material";
import TaskNotificationItem from "./TaskNotificationItem";
import { getTimeAgo } from "../../utils/utils";
import SlideOver from "../Common/SlideOver";
import ActionItem from "./ActionItem";
import CommonService from "../../services/common/commonService";
import { UserNotificationsResponse } from "../../utils/types";
import useDisplaySnackbar from "../../utils/useDisplaySnackbar";
import useGlobalContext from "../../context/useGlobalContext";
import { QueryObserverResult, RefetchOptions } from "@tanstack/react-query";
import { MoreHoriz as MoreHorizIcon } from "@mui/icons-material";
import AllReadIcon from "../../assets/action-items/all_read_icon.svg";
import { useNavigate } from "react-router-dom";

interface TaskNotificationsProps {
  onClose?: () => void;
  userNotification: {
    notificationCount: number;
    getNotificationCountRefetch: (options?: RefetchOptions) => Promise<QueryObserverResult<any, any>>;
    decrementNotificationCount: (amount?: number) => void;
}
}

const defaultNotificationData: UserNotificationsResponse = {
  assigneeNotificationsCount: 0,
  dueDateNotificationsCount: 0,
  deletedTaskNotificationsCount: 0,
  commentMentionNotificationsCount: 0,
  redClientRiskNotificationsCount: 0,
  statusNotificationsCount: 0,
  clientHealthTransitionNotificationsCount: 0,
  assigneeNotifications: [],
  dueDateNotifications: [],
  deletedTaskNotifications: [],
  commentMentionNotifications: [],
  redClientRiskNotifications: [],
  statusNotifications: [],
  clientHealthTransitionNotifications: [],
};

const TaskNotifications: React.FC<TaskNotificationsProps> = ({ userNotification, onClose }) => {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState("account-updates");
  const [subTab, setSubTab] = useState("new-tasks");
  const [taskId, setTaskId] = useState<string>("");
  const [taskIsSubTask, setTaskIsSubTask] = useState(false);
  const [isActionItemValid, setIsActionItemValid] = useState(false);
  const [openSlideover, setOpenSlideOver] = useState(false);
  const [saveTrigger, setSaveTrigger] = useState(false);
  const [discardTrigger, setDiscardTrigger] = useState(false);
  const [userNotificationData, setUserNotificationData] =
    useState<UserNotificationsResponse>(defaultNotificationData);
  const { showSnackbar } = useDisplaySnackbar();
  const selectedActionItemMemo = React.useMemo(
    () => (taskId ? { id: taskId, isSubTask: taskIsSubTask } : null),
    [taskId, taskIsSubTask]
  );
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(menuAnchorEl);
  const { featureMap, accountDetails } = useGlobalContext();
  const userProfileOrgId =  accountDetails?.profileOrgId ?? "";
  const isNewTaskTabEnabled = featureMap?.new_task === true;
  const isDueDateTabEnabled = featureMap?.due_date_changed_task === true;
  const isDeletedTabEnabled = featureMap?.deleted_tasks === true;
  const isMentionTabEnabled = featureMap?.mention_tasks === true;
  const isActionCenterEnabled = featureMap?.client_tasks === true;
  const isStatusChangeTabEnabled = featureMap?.status_change_tasks === true;
  const isAccountUpdatesEnabled = featureMap?.account_updates === true;
  const [readNotifications, setReadNotifications] = useState<string[]>([]);
  const markReadMutation = CommonService.usePostNotificationRead();
  const {
    isFetching: isNotificationFetching,
    isLoading: isNotificationsLoading,
    isSuccess: isNotificationSuccess,
    isError: isNotificationError,
    error: notificationError,
    data: notificationData,
  } = CommonService.useGetUserNotifications(
    {
      userProfileOrgId: userProfileOrgId,
    },
    !!userProfileOrgId
  );

  useEffect(() => {
    if (isNotificationSuccess) {
      const userNotificationList = notificationData?.data;
      setUserNotificationData(userNotificationList || []);
    } else if (isNotificationError) {
      showSnackbar("error", notificationError?.message);
    }
  }, [
    isNotificationFetching,
    isNotificationsLoading,
    isNotificationSuccess,
    isNotificationError,
    notificationData,
  ]);

  const handleReadTaskSuccess = (notificationId: string)=>{
    setReadNotifications((prev) => [...prev, notificationId]);
    userNotification.decrementNotificationCount(1);
    // 2. Reduce count based on tab type
    setUserNotificationData((prev) => {
      if (!prev) return prev;

      const updated = { ...prev };

      // Determine which category this notification belongs to
      const isAssignee = prev.assigneeNotifications.some(
        (n) => n.notificationId === notificationId
      );
      const isDueDate = prev.dueDateNotifications.some(
        (n) => n.notificationId === notificationId
      );
      const isDeleted = prev.deletedTaskNotifications.some(
        (n) => n.notificationId === notificationId
      );
      const isMention = prev.commentMentionNotifications.some(
        (n) => n.notificationId === notificationId
      );
      const isClientTask = prev.redClientRiskNotifications.some(
        (n) => n.notificationId === notificationId
      );

      const isStatusChange = prev.statusNotifications.some (
        (n) => n.notificationId === notificationId
      )

      const isAccountUpdate = prev.clientHealthTransitionNotifications.some (
        (n) => n.notificationId === notificationId
      );

      if (isAssignee) {
        updated.assigneeNotificationsCount =
          Math.max(0, updated.assigneeNotificationsCount - 1);
      }
      if (isDueDate) {
        updated.dueDateNotificationsCount =
          Math.max(0, updated.dueDateNotificationsCount - 1);
      }
      if (isDeleted) {
        updated.deletedTaskNotificationsCount =
          Math.max(0, updated.deletedTaskNotificationsCount - 1);
      }
      if(isMention) {
        updated.commentMentionNotificationsCount =
         Math.max(0, updated.commentMentionNotificationsCount - 1);
      }
      if(isClientTask) {
        updated.redClientRiskNotificationsCount =
          Math.max(0, updated.redClientRiskNotificationsCount - 1);
      }
      if(isStatusChange) {
        updated.statusNotificationsCount = 
          Math.max(0, updated.statusNotificationsCount -1);
      }
      if(isAccountUpdate) {
        updated.clientHealthTransitionNotificationsCount =
          Math.max(0, updated.clientHealthTransitionNotificationsCount - 1);
      }

      return updated;
    });
  }

  const handleTaskClick = (taskId: string, notificationId: string, isSubTask?: boolean) => {
    setTaskId(taskId);
    setTaskIsSubTask(Boolean(isSubTask));
    setOpenSlideOver(true);
    markReadMutation.mutate(
      {
        userProfileOrgId: userProfileOrgId,
        notificationIds: [notificationId],
      },
      {
        onSuccess: () => {
          handleReadTaskSuccess(notificationId);
        },
        onError: () => {
          showSnackbar("error", "Failed to mark notification as read");
        },
      }
    );
  };

  const markNotificationAsRead = (notificationId?: string) => {
    if (!notificationId) return;

    markReadMutation.mutate(
      {
        userProfileOrgId,
        notificationIds: [notificationId],
      },
      {
        onSuccess: () => {
          handleReadTaskSuccess(notificationId);
        },
      }
    );
  };

  const getPendingActionCount = (item: any) =>
    item?.pendingTaskCount ??
    0;

  const isWithin24Hours = (createdAt?: string) => {
    if (!createdAt) return false;
    return Date.now() - new Date(createdAt).getTime() <= 24 * 60 * 60 * 1000;
  };

  const handleClientOverviewNavigation = (
    item: any,
    clientOverviewTab: "fingertipMetrics" | "actionItems"
  ) => {
    const clientDomain = item?.clientName;
    if (!clientDomain) return;

    sessionStorage.setItem("selectedClient", clientDomain);
    markNotificationAsRead(item?.notificationId);
    onClose?.();

    navigate("/accounts/customer", {
      state: {
        clientDomain,
        orgId: item?.orgId,
        subOrgId: item?.subOrgId,
        slideOverParent: "account-parent-page",
        clientOverviewTab,
      },
    });
  };

  const markDeletedAsRead = () => {
    const deletedItems = userNotificationData.deletedTaskNotifications;
    if (!deletedItems || deletedItems?.length === 0) return;

    const unreadItems = deletedItems.filter(
      (item: any) => !item.isRead && !readNotifications.includes(item.notificationId)
    );
    if (unreadItems?.length === 0) return;

    const unreadIds = unreadItems.map((item: any) => item.notificationId);
    const ids = deletedItems.map((item: any) => item.notificationId);

    markReadMutation.mutate(
      {
        userProfileOrgId,
        notificationIds: ids, // send all IDs
      },
      {
        onSuccess: () => {
          // Mark all as read locally so UI updates instantly
          setReadNotifications((prev) => [...prev, ...ids]);
          userNotification.decrementNotificationCount(unreadIds.length);
          setUserNotificationData((prev) => {
            if (!prev) return prev;
            const updated = { ...prev };
  
            updated.deletedTaskNotificationsCount = Math.max(
              0,
              updated.deletedTaskNotificationsCount - unreadIds.length
            );
  
            return updated;
          });
        },
        onError: () => {
          showSnackbar("error", "Failed to mark deleted tasks as read");
        },
      }
    );
  };
  const handleSave = () => setSaveTrigger(true);
  const handleDiscard = () => {
    (setDiscardTrigger(true), 
    setOpenSlideOver(false));
  };

  const handleClose = () => {
    setOpenSlideOver(false);
    setTaskId("");
    setTaskIsSubTask(false);
  };

  const handleMenuOpen = (event: any) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleMarkAllAsRead = () => {
    let notificationsToMark: any[] = [];

    if (tabValue === "updates") {
      notificationsToMark = [
        ...(userNotificationData.assigneeNotifications || []),
        ...(userNotificationData.dueDateNotifications || []),
        ...(userNotificationData.deletedTaskNotifications || []),
        ...(userNotificationData.statusNotifications || []),
      ];
    } else if (tabValue === "updates" && subTab === "due-date-tasks") {
      notificationsToMark = userNotificationData.dueDateNotifications;
    } else if (tabValue === "updates" && subTab === "delete-tasks") {
      notificationsToMark = userNotificationData.deletedTaskNotifications;
    } else if (tabValue === "updates" && subTab === "status-change-tasks") {
      notificationsToMark = userNotificationData.statusNotifications;
    } else if (tabValue === "mention-tasks") {
      notificationsToMark = userNotificationData.commentMentionNotifications;
    } else if (tabValue === "client-tasks") {
      notificationsToMark = userNotificationData.redClientRiskNotifications;
    } else if (tabValue === "account-updates") {
      notificationsToMark = userNotificationData.clientHealthTransitionNotifications;
    }

    if (!notificationsToMark.length) {
      handleMenuClose();
      return;
    }

    const unread = notificationsToMark.filter(
      (n) => !n.isRead && !readNotifications.includes(n.notificationId)
    );

    if (!unread.length) {
      handleMenuClose();
      return;
    }

    const ids = unread.map((n) => n.notificationId);

    markReadMutation.mutate(
      {
        userProfileOrgId,
        notificationIds: ids,
      },
      {
        onSuccess: () => {
          // Mark locally as read
          setReadNotifications((prev) => [...prev, ...ids]);

          // Decrement global count
          userNotification.decrementNotificationCount(ids.length);

          // Update tab counts
          setUserNotificationData((prev) => {
            if (!prev) return prev;
            const updated = { ...prev };

            if (tabValue === "updates") {
              updated.assigneeNotificationsCount = 0;
              updated.dueDateNotificationsCount = 0;
              updated.deletedTaskNotificationsCount = 0;
              updated.statusNotificationsCount = 0;
            }
            if (tabValue === "mention-tasks") {
              updated.commentMentionNotificationsCount = 0;
            }
            if (tabValue === "client-tasks") {
              updated.redClientRiskNotificationsCount = 0;
            }
            if (tabValue === "account-updates") {
              updated.clientHealthTransitionNotificationsCount = 0;
            }

            return updated;
          });

          handleMenuClose(); // ✅ close popper
        },
        onError: () => {
          showSnackbar("error", "Failed to mark all as read");
          handleMenuClose();
        },
      }
    );
  };

  useEffect(() => {
    const availableMainTabs = [
      "updates",
      isMentionTabEnabled && "mention-tasks",
      isActionCenterEnabled && "client-tasks",
      isAccountUpdatesEnabled && "account-updates",
    ].filter(Boolean);

    if (!availableMainTabs.includes(tabValue)) {
      setTabValue(availableMainTabs[0] as string);
    }
  }, [isMentionTabEnabled, isActionCenterEnabled, tabValue]);
  const isLoadingState = isNotificationFetching || isNotificationsLoading;

  const subTabsConfig = [
    {
      key: "new-tasks",
      label: "New Tasks",
      enabled: isNewTaskTabEnabled,
      count: userNotificationData.assigneeNotificationsCount,
    },
    {
      key: "due-date-tasks",
      label: "Due Date Changes",
      enabled: isDueDateTabEnabled,
      count: userNotificationData.dueDateNotificationsCount,
    },
    {
      key: "delete-tasks",
      label: "Deleted Tasks",
      enabled: isDeletedTabEnabled,
      count: userNotificationData.deletedTaskNotificationsCount,
    },
    {
      key: "status-change-tasks",
      label: "Status",
      enabled: isStatusChangeTabEnabled,
      count: userNotificationData.statusNotificationsCount,
    },
  ];

  const subTabContentConfig: Record<
    string,
    {
      list: any[];
      emptyText: string;
      getTitle: (item?: any) => string;
      extraProps?: Record<string, any>;
    }
  > = {
    "new-tasks": {
      list: userNotificationData.assigneeNotifications,
      emptyText: "No new task notifications",
      getTitle: (item: any) =>
        item?.fieldName === "TASK_ASSIGNED"
          ? "assigned you a task."
          : "updated the assignee.",
      extraProps: {
        onDescriptionClick: handleTaskClick,
      },
    },

    "due-date-tasks": {
      list: userNotificationData.dueDateNotifications,
      emptyText: "No due date changes",
      getTitle: () => "updated the due date.",
      extraProps: {
        onDescriptionClick: handleTaskClick,
      },
    },

    "delete-tasks": {
      list: userNotificationData.deletedTaskNotifications,
      emptyText: "No deleted tasks",
      getTitle: () => "deleted the task.",
      extraProps: {
        isDeleted: true,
      },
    },

    "status-change-tasks": {
      list: userNotificationData.statusNotifications,
      emptyText: "No status changed tasks",
      getTitle: () => "updated the Status to",
      extraProps: {
        onDescriptionClick: handleTaskClick,
      }
    },
  };

  const currentTab = subTabContentConfig[subTab as keyof typeof subTabContentConfig];
  const taskUpdatesCount =
  (userNotificationData.assigneeNotificationsCount || 0) +
  (userNotificationData.dueDateNotificationsCount || 0) +
  (userNotificationData.deletedTaskNotificationsCount || 0) +
  (userNotificationData.statusNotificationsCount || 0);

  return (
    <>
      <div className="taskNotifications-wrapper">
        {/* --- Tabs Header --- */}
        <div className="notification_tabs_wrapper">
          <Tabs
            value={tabValue}
            onChange={(_e, newValue) => {
              setTabValue(newValue);

              if (newValue === "delete-tasks") {
                markDeletedAsRead();
              }
            }}
            className="app-tabs-container notification_tabs"
          >
            {isAccountUpdatesEnabled && (
              <Tab value="account-updates" label={
                <div className="tab-label-with-count">
                  Account Updates
                  {!isLoadingState && userNotificationData.clientHealthTransitionNotificationsCount > 0 && (
                    <span
                      className={
                        tabValue === "account-updates"
                          ? "tab-badge tab-badge-selected"
                          : "tab-badge"
                      }
                    >
                      {userNotificationData.clientHealthTransitionNotificationsCount}
                    </span>
                  )}
                </div>
              }
              />
            )}
            <Tab
              value="updates"
              label={
                <div className="tab-label-with-count">
                  Task Updates
                  {!isLoadingState && taskUpdatesCount > 0 && (
                    <span
                      className={
                        tabValue === "updates"
                          ? "tab-badge tab-badge-selected"
                          : "tab-badge"
                      }
                    >
                      {taskUpdatesCount}
                    </span>
                  )}
                </div>
              }
            />
            {isMentionTabEnabled && (
              <Tab
                value="mention-tasks"
                label={
                  <div className="tab-label-with-count">
                    Mentions
                    {!isLoadingState && userNotificationData.commentMentionNotificationsCount > 0 && (
                      <span
                        className={
                          tabValue === "mention-tasks"
                            ? "tab-badge tab-badge-selected"
                            : "tab-badge"
                        }
                      >
                        {userNotificationData.commentMentionNotificationsCount}
                      </span>
                    )}
                  </div>
                }
              />
            )}
            {isActionCenterEnabled && (
              <Tab
                value="client-tasks"
                label={
                  <div className="tab-label-with-count">
                    Action Center
                    {!isLoadingState && userNotificationData.redClientRiskNotificationsCount > 0 && (
                      <span
                        className={
                          tabValue === "client-tasks"
                            ? "tab-badge tab-badge-selected"
                            : "tab-badge"
                        }
                      >
                        {userNotificationData.redClientRiskNotificationsCount}
                      </span>
                    )}
                  </div>
                }
              />
            )}
          </Tabs>

          <Box className={`icon-hover-box ${isMenuOpen ? "icon-active" : ""}`}>
          <MoreHorizIcon
            fontSize="medium"
            onClick={handleMenuOpen}
            >
          </MoreHorizIcon>
          </Box>
          <Menu
            anchorEl={menuAnchorEl}
            open={isMenuOpen}
            onClose={handleMenuClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            PaperProps={{
              sx: {
                borderRadius: "8px",
                px: 1,
                mt:2,
              },
            }}
          >
            <MenuItem   
            onClick={handleMarkAllAsRead}
            sx={{ 
              fontSize: "12px", 
              padding: 0,
              cursor: "pointer",
              gap: "4px",
            }}>
              <img src={AllReadIcon} alt="read"/>
              <span style={{marginTop:1}}>Mark all as Read</span>
            </MenuItem>
          </Menu>
        </div>

        {tabValue === "updates" && (
        <div className="sub-tabs-pill-wrapper">
          {subTabsConfig
            .filter((tab) => tab.enabled)
            .map((tab) => {
              const isActive = subTab === tab.key;

              return (
                <button
                  key={tab.key}
                  className={`sub-pill ${isActive ? "active" : ""}`}
                  onClick={() => {
                    setSubTab(tab.key);
                  }}
                >
                  {tab.label}

                  {tab.count > 0 && (
                    <span className={`pill-count ${isActive ? "active" : ""}`}>
                      {tab.count}
                    </span>
                  )}
                </button>
              );
            })}
        </div>
        )}
        {/* --- Tab Panels --- */}
        <div className="notification_tab_panel">
          {isLoadingState ? (
            <div
              className="flex justify-center items-center"
              style={{ padding: "40px 0" }}
            >
              <CircularProgress
                sx={{ color: "#ccc", opacity: 0.5 }}
                thickness={5}
                size={60}
              />
            </div>
          ) : (
            <>
              {tabValue === "updates" && (
                <>

                  {/* 🔹 CONTENT */}
                  {(!currentTab.list || currentTab.list.length === 0) && (
                    <div className="no-notification">{currentTab.emptyText}</div>
                  )}

                  {/* List */}
                  {currentTab.list?.map((item: any) => (
                    <TaskNotificationItem
                      key={item?.notificationId}
                      userName={item?.updatedByName}
                      avatar={item?.updatedByPictureUrl}
                      title={currentTab.getTitle(item)}
                      timeAgo={getTimeAgo(item?.createdAt)}
                      description={item?.taskTitle}
                      taskId={item?.taskId}
                      notificationId={item?.notificationId}
                      unread={
                        !item?.isRead && !readNotifications.includes(item.notificationId)
                      }
                      statusValue={item?.newValue}
                      {...(currentTab.extraProps || {})}
                      isSubTask={item?.isSubTask}
                    />
                  ))}
                </>
              )}
              {tabValue === "mention-tasks" && isMentionTabEnabled && (
                <>
                  {userNotificationData?.commentMentionNotifications?.length ===
                    0 && (
                    <div className="no-notification">
                      No mention task notifications
                    </div>
                  )}

                  {userNotificationData?.commentMentionNotifications?.map(
                    (item: any) => (
                      <TaskNotificationItem
                        key={item?.notificationId}
                        userName={item?.updatedByName}
                        avatar={item?.updatedByPictureUrl}
                        title={"mentioned you in a task."}
                        timeAgo={getTimeAgo(item?.createdAt)}
                        description={item?.taskTitle}
                        onDescriptionClick={handleTaskClick}
                        taskId={item?.taskId}
                        notificationId={item?.notificationId}
                        unread={
                          !item?.isRead && !readNotifications.includes(item.notificationId)
                        }
                        isSubTask={item?.isSubTask}
                      />
                    )
                  )}
                </>
              )}
              {tabValue === "client-tasks" && isActionCenterEnabled && (
                <>
                  {userNotificationData?.redClientRiskNotifications?.length ===
                    0 && (
                    <div className="no-notification">
                      No task notifications
                    </div>
                  )}
                  {userNotificationData?.redClientRiskNotifications?.map(
                    (item: any) => (
                      <TaskNotificationItem
                        key={item?.notificationId}
                        userName={item?.clientName || item?.updatedByName || ""}
                        avatar={item?.updatedByPictureUrl}
                        title={"is now at risk"}
                        timeAgo={getTimeAgo(item?.createdAt)}
                        description={item?.taskTitle || ""}
                        variant="action-center"
                        onClientNameClick={() =>
                          handleClientOverviewNavigation(item, "fingertipMetrics")
                        }
                        onActionCountClick={() =>
                          handleClientOverviewNavigation(item, "actionItems")
                        }
                        pendingActionLabel={`${getPendingActionCount(item)} actions pending -> Review & act now`}
                        showNewTag={isWithin24Hours(item?.createdAt)}
                        unread={
                          !item?.isRead && !readNotifications.includes(item.notificationId)
                        }
                        orgId={item?.orgId}
                        subOrgId={item.subOrgId}
                        isSubTask={item?.isSubTask}
                      />
                    )
                  )}
                </>
              )}
              {tabValue === "account-updates" && isAccountUpdatesEnabled && (
                <>
                  {userNotificationData?.clientHealthTransitionNotifications?.length ===
                    0 && (
                    <div className="no-notification">
                      No account updates notifications
                    </div>
                  )}
                  {userNotificationData?.clientHealthTransitionNotifications?.map(
                    (item: any) => (
                      <TaskNotificationItem
                        key={item?.notificationId}
                        userName={item?.clientName || ""}
                        avatar={item?.updatedByPictureUrl}
                        title={"Account Rating Dropped"}
                        timeAgo={getTimeAgo(item?.createdAt)}
                        description={item?.taskTitle || ""}
                        variant="account-updates"
                        onClientNameClick={() =>
                          handleClientOverviewNavigation(item, "fingertipMetrics")
                        }
                        showNewTag={isWithin24Hours(item?.createdAt)}
                        unread={
                          !item?.isRead && !readNotifications.includes(item.notificationId)
                        }
                        orgId={item?.orgId}
                        subOrgId={item.subOrgId}
                        oldValue={item?.oldValue}
                        newValue={item?.newValue}
                        isSubTask={item?.isSubTask}
                      />
                    )
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
      <SlideOver
        isOpen={openSlideover}
        onClose={() => {
          setOpenSlideOver(false);
        }}
        showHeaderButtons={false}
        onSave={handleSave}
        onDiscard={handleDiscard}
        discard={taskId != null ? false : true}
        saveDisabled={!isActionItemValid}
        closeOnRight
      >
        <ActionItem
          actionItemType={"MY_ACTION_ITEMS"}
          saveTrigger={saveTrigger}
          discardTrigger={discardTrigger}
          resetSaveTrigger={() => setSaveTrigger(false)}
          resetDiscardTrigger={() => setDiscardTrigger(false)}
          openDialog={openSlideover}
          onValidityChange={setIsActionItemValid}
          setOpenDialog={setOpenSlideOver}
          handleClose={handleClose}
          selectedActionItem={selectedActionItemMemo}
          tagsList={[]}
          spacesList={[]}
          attendees={[]}
        />
      </SlideOver>
    </>
  );
};

export default TaskNotifications;
